import { isNil } from 'lodash-es';
import {
  required,
  minLength,
  maxLength,
  minValue,
  maxValue,
  numeric,
  email,
  regex,
} from '@regle/rules';
import type { RegleRuleDefinition } from '@regle/core';

export type ValidationErrors = Record<string, string>;

interface ParsedRule {
  name: string;
  params: any[];
}

function callRule(
  rule: RegleRuleDefinition<any, any, any>,
  value: any,
): boolean {
  // @ts-ignore
  return rule.validator(value, undefined, undefined);
}

function getRegleRule(
  name: string,
  params: any[],
): RegleRuleDefinition<any, any, any> | null {
  switch (name) {
    case 'required':
      return required;
    case 'email':
      return email;
    case 'numeric':
      return numeric;

    case 'min':
    case 'minLength':
      return minLength(Number(params[0]));

    case 'max':
    case 'maxLength':
      return maxLength(Number(params[0]));

    case 'minValue':
    case 'min_value':
      return minValue(Number(params[0]));

    case 'maxValue':
    case 'max_value':
      return maxValue(Number(params[0]));

    case 'regex':
      return regex(new RegExp(params[0]));

    default:
      return null;
  }
}

const customValidators: Record<string, (value: any, params: any[]) => boolean> =
  {
    in: (value, params) => {
      const options = params[0].split('|');
      return options.includes(String(value));
    },
    double: (value) => {
      if (isNil(value) || value === '') {
        return true;
      }
      return /^-?\d*(\.\d+)?$/.test(String(value));
    },
  };

export function parseValidationRules(
  validator?: string | string[],
  isRequired?: boolean,
): ParsedRule[] {
  const rules: ParsedRule[] = [];

  if (isRequired) {
    rules.push({ name: 'required', params: [] });
  }

  if (!validator) {
    return rules;
  }

  const ruleStrings = Array.isArray(validator)
    ? validator
    : validator.split(';');

  for (const ruleStr of ruleStrings) {
    if (!ruleStr) {
      continue;
    }
    const [name, args] = ruleStr.split(':');
    const params = args ? args.split(',') : [];

    if (name === 'required' && isRequired) {
      continue;
    }

    rules.push({ name, params });
  }

  return rules;
}

export function validateValue(
  value: any,
  rules: ParsedRule[],
  fieldLabel: string = 'Field',
): string | null {
  for (const rule of rules) {
    let isValid = true;

    if (rule.name !== 'required' && (isNil(value) || value === '')) {
      continue;
    }

    const regleRule = getRegleRule(rule.name, rule.params);
    if (regleRule) {
      isValid = callRule(regleRule, value);
    } else if (customValidators[rule.name]) {
      isValid = customValidators[rule.name](value, rule.params);
    } else {
      console.warn(`[DataTable] Unknown validator rule: ${rule.name}`);
    }

    if (!isValid) {
      return getErrorMessage(rule.name, fieldLabel, rule.params);
    }
  }
  return null;
}

function getErrorMessage(
  ruleName: string,
  label: string,
  params: any[],
): string {
  switch (ruleName) {
    case 'required':
      return `${label} is required.`;
    case 'min':
    case 'minLength':
      return `${label} must be at least ${params[0]} characters.`;
    case 'max':
    case 'maxLength':
      return `${label} must be at most ${params[0]} characters.`;
    case 'minValue':
    case 'min_value':
      return `${label} must be at least ${params[0]}.`;
    case 'maxValue':
    case 'max_value':
      return `${label} must be at most ${params[0]}.`;
    case 'numeric':
    case 'double':
      return `${label} must be a valid number.`;
    case 'email':
      return `${label} must be a valid email.`;
    case 'in':
      return `${label} must be one of: ${params[0].split('|').join(', ')}`;
    case 'regex':
      return `${label} format is invalid.`;
    default:
      return `${label} is invalid.`;
  }
}
